import { curry, pick, prop } from 'ramda'
import { createSelector } from 'redux-bundler'

import getGlobal from '~/src/Lib/getGlobal'
import { getIsMobileLayout, subscribeMobileLayout } from '~/src/Lib/mobileLayout'
import { debounce, defer, deferred, deferUntil, EMPTY_OBJECT, shallowEquals } from '~/src/Lib/Utils'

export const DIMENSIONS_UPDATED = 'DIMENSIONS_UPDATED'
export const DIMENSIONS_ORIENTATION_PORTRAIT = 'DIMENSIONS_ORIENTATION_PORTRAIT'
export const DIMENSIONS_ORIENTATION_LANDSCAPE = 'DIMENSIONS_ORIENTATION_LANDSCAPE'

type Orientation = typeof DIMENSIONS_ORIENTATION_PORTRAIT | typeof DIMENSIONS_ORIENTATION_LANDSCAPE

// Sizes in px keyed by `window`, `content` and the data-trackwidth / data-trackheight value of each tracked element.
type AxisSizes = Record<string, number>

interface Scroll {
  x: number | undefined
  y: number | undefined
}

// A type alias, not an interface: the reducer reads it as a Record<string, unknown>, which interfaces do not allow
type DimensionsState = {
  height: AxisSizes
  mobile: boolean
  orientation: Orientation
  scroll: Scroll
  width: AxisSizes
}

type DimensionsPayload = Partial<DimensionsState>

interface DimensionsAction {
  payload: DimensionsPayload
  type: typeof DIMENSIONS_UPDATED
}

type Dispatch = (action: DimensionsAction) => void

interface DimensionsStore {
  dispatch: Dispatch
  selectHeights: () => AxisSizes
  selectRouteInfo: () => { url: string }
  selectWidths: () => AxisSizes
}

interface BundleContext {
  dispatch: Dispatch
  store: DimensionsStore
}

const CONTENT_HEIGHT_SUBTRAHENDS = ['appBar', 'prodBanner', 'versionBanner']
const CONTENT_WIDTH_SUBTRAHENDS = ['navRail']
// getGlobal is untyped JS and can only return null when no global object exists; the fallback narrows its type
const global = getGlobal() ?? globalThis
// There is no DOM outside the browser (SSR, node tests)
const document: Document | undefined = global.document
const getScrollParent = () => document?.querySelector('main#content')
const calcOrientation = (height = 0, width = 0): Orientation =>
  height > width ? DIMENSIONS_ORIENTATION_PORTRAIT : DIMENSIONS_ORIENTATION_LANDSCAPE

const trackWidth = (initial: AxisSizes = { window: global.innerWidth, content: global.innerWidth }): AxisSizes => {
  let payload = initial
  const tracked = document?.querySelectorAll('[data-trackwidth]') ?? []

  const { content: oldContent, window: oldWindow, ...oldTracked } = payload
  if (oldWindow !== global.innerWidth) {
    payload = { ...payload, window: global.innerWidth }
  }
  if (!tracked.length) return payload

  const fullRefresh = Object.keys(oldTracked).length !== tracked.length

  payload = Array.from(tracked).reduce<AxisSizes>(
    (acc, el) => {
      const key = el.getAttribute('data-trackwidth') ?? ''
      if (!fullRefresh && !key) return acc
      const { [key]: oldWidth } = acc
      const newWidth = el.getBoundingClientRect().width
      if (oldWidth === newWidth) return acc
      return { ...acc, [key]: newWidth }
    },
    fullRefresh ? { window: payload.window, content: payload.content } : payload,
  )

  const content =
    payload.window < 960 || document?.querySelector('#wrapper.fullscreen')
      ? payload.window
      : payload.window - CONTENT_WIDTH_SUBTRAHENDS.reduce((acc, key) => acc + (payload[key] || 0), 0)

  if (oldContent !== content) {
    payload = { ...payload, content }
  }

  return payload
}

const trackHeight = (initial: AxisSizes = { window: global.innerHeight, content: global.innerHeight }): AxisSizes => {
  let payload = initial
  const tracked = document?.querySelectorAll('[data-trackheight]')

  const { content: oldContent, window: oldWindow, ...oldTracked } = payload
  if (oldWindow !== global.innerHeight) {
    payload = { ...payload, window: global.innerHeight }
  }

  if (!tracked?.length) return payload

  const fullRefresh = Object.keys(oldTracked).length !== tracked.length

  payload = Array.from(tracked).reduce<AxisSizes>(
    (acc, el) => {
      const key = el.getAttribute('data-trackheight') ?? ''
      if (!key) return acc
      const { [key]: oldHeight } = acc
      const newHeight = el.getBoundingClientRect().height

      if (oldHeight === newHeight) return acc

      return {
        ...acc,
        [key]: newHeight,
      }
    },
    fullRefresh ? { window: payload.window, content: payload.content } : payload,
  )

  const content = payload.window - CONTENT_HEIGHT_SUBTRAHENDS.reduce((acc, key) => acc + (payload[key] || 0), 0)

  if (oldContent !== content) {
    payload = { ...payload, content }
  }

  return payload
}

const trackScroll = (): Scroll => {
  const scrollParent = getScrollParent()

  return { x: scrollParent?.scrollLeft, y: scrollParent?.scrollTop }
}

const trackOrientation = (w: Pick<Window, 'innerHeight' | 'innerWidth'> = global): Orientation =>
  calcOrientation(w.innerHeight, w.innerWidth)

const dispatcher: (dispatch: Dispatch, payload: DimensionsPayload) => void = debounce(
  (dispatch: Dispatch, payload: DimensionsPayload) =>
    dispatch({
      type: DIMENSIONS_UPDATED,
      payload,
    }),
  250,
)

const defaultState: DimensionsState = {
  width: trackWidth(),
  height: trackHeight(),
  orientation: trackOrientation(),
  scroll: trackScroll(),
  mobile: getIsMobileLayout(),
}

// Checks the shape only: at least one known key is present, not that its value is valid
const isDimensionsPayload = (payload: unknown): payload is DimensionsPayload =>
  typeof payload === 'object' && payload !== null && Object.keys(defaultState).some(key => key in payload)

const isUrlAction = (action: unknown): action is { payload: { url: string } } =>
  typeof action === 'object' &&
  action !== null &&
  'payload' in action &&
  typeof action.payload === 'object' &&
  action.payload !== null &&
  'url' in action.payload &&
  typeof action.payload.url === 'string'

export default {
  name: 'dimensions',
  getMiddleware: () => {
    let timeout: number | null = null
    return curry((store: DimensionsStore, next: (action: unknown) => void, action: unknown) => {
      if (!isUrlAction(action)) {
        next(action)
        return
      }
      if (timeout) {
        clearTimeout(timeout)
      }
      const { url: prevUrl } = store.selectRouteInfo()
      const {
        payload: { url },
      } = action

      const [, suffix] = url.split(location.host)
      const [path] = suffix.split('?')
      if (path !== prevUrl) {
        timeout = defer(() => {
          timeout = null
          dispatcher(store.dispatch, {
            width: trackWidth(store.selectWidths()),
            height: trackHeight(store.selectHeights()),
            orientation: trackOrientation(),
          })
        }, 500)
      }
      next(action)
    })
  },
  init: (store: DimensionsStore): (() => void) | undefined => {
    if (!global.addEventListener) {
      return undefined
    }
    const unsubscribeMobile = subscribeMobileLayout(() =>
      store.dispatch({ type: DIMENSIONS_UPDATED, payload: { mobile: getIsMobileLayout() } }),
    )
    const resizeHandler = debounce(() => {
      store.dispatch({
        type: DIMENSIONS_UPDATED,
        payload: {
          width: trackWidth(store.selectWidths()),
          height: trackHeight(store.selectHeights()),
          orientation: trackOrientation(),
        },
      })
    }, 250)
    const deferredResizeHandler = deferred(resizeHandler, 1000)

    global?.addEventListener('resize', resizeHandler)
    global?.addEventListener('orientationchange', deferredResizeHandler)

    const scrollHandler = debounce(() => {
      store.dispatch({
        type: DIMENSIONS_UPDATED,
        payload: {
          scroll: trackScroll(),
        },
      })
    }, 150)

    deferUntil(
      () => {
        getScrollParent()?.addEventListener('scroll', scrollHandler)
        scrollHandler()
      },
      getScrollParent,
      33,
    )

    return () => {
      unsubscribeMobile()
      global.removeEventListener('resize', resizeHandler)
      global.removeEventListener('orientationchange', deferredResizeHandler)
      getScrollParent()?.removeEventListener('scroll', scrollHandler)
    }
  },
  reducer: (
    state: Readonly<DimensionsState> = defaultState,
    action: DimensionsAction | { type?: undefined } = {},
  ): Readonly<DimensionsState> => {
    if (action.type === DIMENSIONS_UPDATED) {
      // Payload keys are compared by name, so view both sides as string-keyed records
      const current: Readonly<Record<string, unknown>> = state
      const incoming: Readonly<Record<string, unknown>> = action.payload
      const changed = Object.keys(incoming).filter(key => !shallowEquals(current[key], incoming[key]))

      if (changed.length > 0) {
        return {
          ...state,
          ...pick(changed, incoming),
        }
      }
    }
    return state
  },
  doUpdateDimensions:
    (payload?: unknown) =>
    ({ dispatch, store }: BundleContext) =>
      dispatcher(
        dispatch,
        isDimensionsPayload(payload)
          ? payload
          : {
              width: trackWidth(store.selectWidths()),
              height: trackHeight(store.selectHeights()),
              orientation: trackOrientation(),
            },
      ),
  doUpdateHeights:
    () =>
    ({ dispatch, store }: BundleContext) =>
      dispatcher(dispatch, {
        height: trackHeight(store.selectHeights()),
      }),
  doUpdateWidths:
    () =>
    ({ dispatch, store }: BundleContext) =>
      dispatcher(dispatch, {
        width: trackWidth(store.selectWidths()),
      }),
  doUpdateOrientation:
    () =>
    ({ dispatch }: Pick<BundleContext, 'dispatch'>) =>
      dispatcher(dispatch, {
        orientation: trackOrientation(),
      }),
  selectDimensions: (state: { dimensions: DimensionsState }): DimensionsState => state.dimensions,
  selectWidths: createSelector<DimensionsState, AxisSizes>('selectDimensions', prop('width')),
  selectHeights: createSelector<DimensionsState, AxisSizes>('selectDimensions', prop('height')),
  selectOrientation: createSelector<DimensionsState, Orientation>('selectDimensions', prop('orientation')),
  selectIsLandscape: createSelector(
    'selectOrientation',
    (orientation: Orientation) => orientation === DIMENSIONS_ORIENTATION_LANDSCAPE,
  ),
  selectIsPortrait: createSelector(
    'selectOrientation',
    (orientation: Orientation) => orientation === DIMENSIONS_ORIENTATION_PORTRAIT,
  ),
  selectIsMobile: createSelector<DimensionsState, boolean>('selectDimensions', prop('mobile')),
  reactUpdateDimensions: createSelector('selectDimensions', (dimensions: DimensionsState) => {
    const { width, height, orientation } = dimensions

    let payload: DimensionsPayload = EMPTY_OBJECT
    const newWidth = trackWidth(width)
    if (newWidth !== width) {
      payload = { ...payload, width: newWidth }
    }

    const newHeight = trackHeight(height)
    if (newHeight !== height) {
      payload = { ...payload, height: newHeight }
    }

    const newOrientation = trackOrientation()
    if (newOrientation !== orientation) {
      payload = { ...payload, orientation: newOrientation }
    }

    if (payload !== EMPTY_OBJECT) {
      return { actionCreator: 'doUpdateDimensions', args: [payload] }
    }
    return undefined
  }),
}
